/*
  Earthonmall shared section virtualization registry.

  Load once with defer.
  This version normalizes every managed section and exposes safe per-section
  hydrate/virtualize methods for the page window manager.
*/

(() => {
  if (window.EOMVirtualSections) {
    window.EOMVirtualSections.initAutoSections?.();
    return;
  }

  const controllers = new Map();
  const originalControllers = new WeakMap();
  const wrappedControllers = new WeakMap();
  let scrollFrame = null;

  const VALID_STATES = new Set(["active", "hydrating", "shell", "static"]);
  const VALID_MODES = new Set(["template", "visual", "static", "custom"]);
  const LOADER_CONTRACT_REGISTRY = {
    version: "phase-0.5",
    cssSource: "assets/eom-virtual-sections.css",
    jsSource: "assets/eom-virtual-sections.js",
    types: {
      "hero-media": {
        label: "Hero media outline",
        role: "critical visual shell",
        maxDomNodes: 12,
        fallbackRequired: true
      },
      "format-card-grid": {
        label: "Format card grid shell",
        role: "collection routing shell",
        maxDomNodes: 36,
        fallbackRequired: true
      },
      "ribbon-strip": {
        label: "Savings ribbon strip shell",
        role: "text-forward strip shell",
        maxDomNodes: 12,
        fallbackRequired: true
      },
      "product-card-grid": {
        label: "Product card grid shell",
        role: "card grid and buying shell",
        maxDomNodes: 80,
        fallbackRequired: true
      },
      "promo-panel": {
        label: "Promotional panel shell",
        role: "static promo content shell",
        maxDomNodes: 24,
        fallbackRequired: true
      },
      "subscription-banner": {
        label: "Subscription banner shell",
        role: "subscribe and save promotional shell",
        maxDomNodes: 24,
        fallbackRequired: true
      },
      "feature-panel": {
        label: "Feature panel shell",
        role: "image and copy feature shell",
        maxDomNodes: 28,
        fallbackRequired: true
      },
      "comparison-table": {
        label: "Comparison table shell",
        role: "static comparison shell",
        maxDomNodes: 24,
        fallbackRequired: true
      },
      "quiz-panel": {
        label: "Interactive quiz shell",
        role: "interactive fallback shell",
        maxDomNodes: 32,
        fallbackRequired: true
      },
      "bundle-feature": {
        label: "Featured bundle shell",
        role: "bundle media shell",
        maxDomNodes: 32,
        fallbackRequired: true
      },
      "bundle-grid": {
        label: "Bundle options grid shell",
        role: "bundle card grid shell",
        maxDomNodes: 48,
        fallbackRequired: true
      },
      "logo-strip": {
        label: "Brand logo strip shell",
        role: "brand link shell",
        maxDomNodes: 24,
        fallbackRequired: true
      },
      "story-carousel": {
        label: "Story carousel shell",
        role: "editorial carousel shell",
        maxDomNodes: 36,
        fallbackRequired: true
      },
      "blog-grid": {
        label: "Blog grid shell",
        role: "article card shell",
        maxDomNodes: 42,
        fallbackRequired: true
      },
      "closing-panel": {
        label: "Closing checkout panel shell",
        role: "closing content shell",
        maxDomNodes: 36,
        fallbackRequired: true
      },
      "static-text": {
        label: "Static text shell",
        role: "plain crawlable content",
        maxDomNodes: 8,
        fallbackRequired: true
      },
      "footer-static": {
        label: "Footer/static shell",
        role: "footer or unmanaged static content",
        maxDomNodes: 8,
        fallbackRequired: false
      }
    },
    sectionTypes: {
      "minimalist-hero-with-image": "hero-media",
      "select-a-collection": "format-card-grid",
      "eom-bulk-savings-strip": "ribbon-strip",
      "fast-track-matrix": "product-card-grid",
      "promote-subscription": "subscription-banner",
      "featured-subscription-plan": "feature-panel",
      "nrt-price-comparison": "comparison-table",
      "interactive-quit-quiz": "quiz-panel",
      "featured-nrt-bundle": "bundle-feature",
      "nrt-bundle-options": "bundle-grid",
      "shop-by-brand-logos": "logo-strip",
      "featured-journey": "story-carousel",
      "blog-section": "blog-grid",
      "end-of-page": "closing-panel",
      "_blocks": "footer-static"
    }
  };

  function numberFromDataset(root, key, fallback) {
    const value = Number(root.dataset[key]);
    return Number.isFinite(value) ? value : fallback;
  }

  function isVirtualizationDisabled(root) {
    return root.dataset.eomEnableVirtualization === "false";
  }

  function isSelfManagedMatrix(root) {
    return Boolean(root && root.hasAttribute("data-matrix-card-virtualized-section"));
  }

  function getRootIdentity(root) {
    return [
      root.id,
      root.dataset.sectionId,
      root.dataset.eomSectionId,
      root.dataset.eomSectionName,
      root.dataset.eomSectionKind,
      root.dataset.shopifyEditorSection,
      root.className
    ].filter(Boolean).join(" ").toLowerCase();
  }

  function getSectionTypeHint(root) {
    return String(root.dataset.eomSectionType || root.dataset.sectionType || "").trim().toLowerCase();
  }

  function isPromoteSubscriptionRoot(root) {
    const typeHint = getSectionTypeHint(root);
    const identity = getRootIdentity(root);

    return typeHint === "promote-subscription" ||
      identity.includes("promote_subscription") ||
      identity.includes("promote-subscription") ||
      identity.includes("subscribe-section__");
  }

  function getManagedShellContract(root) {
    if (!root) return null;

    const typeHint = getSectionTypeHint(root);
    const identity = getRootIdentity(root);

    if (typeHint === "minimalist-hero-with-image" ||
      identity.includes("minimalist_hero_with_image") ||
      identity.includes("premium-hero-") ||
      root.hasAttribute("data-virtual-hero")) {
      return { sectionType: "minimalist-hero-with-image", loaderType: "hero-media", className: "eom-hero-media-loader" };
    }

    if (typeHint === "nrt-price-comparison" ||
      identity.includes("nrt_price_comparison") ||
      identity.includes("nrt-price-receipts") ||
      identity.includes("nrt-price-comparison")) {
      return { sectionType: "nrt-price-comparison", loaderType: "comparison-table", className: "eom-comparison-table-loader" };
    }

    if (typeHint === "nrt-bundle-options" ||
      identity.includes("nrt_bundle_options") ||
      identity.includes("nrt-bundle-options") ||
      identity.includes("bundles-section__")) {
      return { sectionType: "nrt-bundle-options", loaderType: "bundle-grid", className: "eom-bundle-grid-loader" };
    }

    if (isPromoteSubscriptionRoot(root)) {
      return { sectionType: "promote-subscription", loaderType: "subscription-banner", className: "eom-subscription-banner-loader" };
    }

    return null;
  }

  function ensureManagedShellLoader(root) {
    const contract = getManagedShellContract(root);
    if (!contract) return;
    if (root.querySelector(`:scope > [data-eom-loader][data-eom-loader-type="${contract.loaderType}"]`)) return;

    const loaderContract = LOADER_CONTRACT_REGISTRY.types[contract.loaderType] || {};
    const loader = document.createElement("div");
    loader.className = contract.className;
    loader.setAttribute("data-eom-loader", "");
    loader.setAttribute("data-eom-loader-registry", "");
    loader.setAttribute("data-eom-loader-type", contract.loaderType);
    loader.setAttribute("data-eom-loader-owner", "registry");
    loader.setAttribute("data-eom-loader-source", LOADER_CONTRACT_REGISTRY.jsSource);
    loader.setAttribute("data-eom-loader-max-dom-nodes", String(loaderContract.maxDomNodes || ""));
    loader.setAttribute("aria-hidden", "true");

    root.dataset.eomCssShell = "true";
    root.dataset.eomLoaderCurrentOwner = "registry";
    root.dataset.eomApprovedLoaderType = contract.loaderType;
    root.dataset.eomApprovedLoaderRegistryVersion = LOADER_CONTRACT_REGISTRY.version;
    if (!root.dataset.eomSectionType) root.dataset.eomSectionType = contract.sectionType;
    root.prepend(loader);
  }

  function hasLockedHeight(root) {
    return root.classList.contains("is-eom-height-locked") || root.classList.contains("is-height-locked");
  }

  function shouldLockHeight(root) {
    return root.dataset.eomNoHeightLock !== "true" &&
      root.dataset.eomVisualOnly !== "true";
  }

  function hasMountedTemplateContent(root) {
    const mount = root.querySelector("[data-eom-virtual-mount]");
    return Boolean(mount && mount.childElementCount);
  }

  function hasMountedVisualContent(root) {
    return Array.from(root.querySelectorAll("[data-eom-visual-mount]")).some((mount) => mount.childElementCount);
  }

  function inferMode(root) {
    const existingMode = root.dataset.eomVirtualMode;

    if (VALID_MODES.has(existingMode)) return existingMode;
    if (isVirtualizationDisabled(root) || root.classList.contains("eom-seo-preserved-section")) return "static";
    if (root.querySelector("[data-eom-virtual-template]") && root.querySelector("[data-eom-virtual-mount]")) return "template";
    if (root.querySelector("[data-eom-visual-slot]")) return "visual";

    return "custom";
  }

  function inferState(root) {
    const existingState = root.dataset.eomVirtualState;

    if (VALID_STATES.has(existingState)) return existingState;
    if (isVirtualizationDisabled(root) || root.dataset.eomVirtualMode === "static") return "active";
    if (hasMountedTemplateContent(root) || hasMountedVisualContent(root)) return "active";
    if (hasLockedHeight(root)) return "shell";

    return "active";
  }

  function normalizeRoot(root) {
    if (!root) return;

    ensureManagedShellLoader(root);

    if (!root.dataset.eomEnableVirtualization) {
      root.dataset.eomEnableVirtualization = root.dataset.eomVirtualMode === "static" ? "false" : "true";
    }

    root.dataset.eomVirtualMode = inferMode(root);
    root.dataset.eomVirtualState = inferState(root);

    if (root.dataset.eomVirtualMode === "static") {
      root.dataset.eomEnableVirtualization = "false";
      root.dataset.eomVirtualState = "active";
    }
  }

  function updatePositionFlags(root, details) {
    root.dataset.eomNearViewport = details.nearViewport ? "true" : "false";
    root.dataset.eomFarFromViewport = details.farAbove || details.farBelow ? "true" : "false";
    root.style.setProperty("--eom-section-top", `${Math.round(details.rect.top)}px`);
  }

  function normalizeAfterHydrate(root) {
    if (!VALID_STATES.has(root.dataset.eomVirtualState)) {
      root.dataset.eomVirtualState = "active";
    }

    if (root.dataset.eomVirtualState === "shell") {
      root.dataset.eomVirtualState = "active";
    }
  }

  function normalizeAfterVirtualize(root) {
    if (isVirtualizationDisabled(root) || root.dataset.eomVirtualMode === "static") {
      root.dataset.eomVirtualState = "active";
      return;
    }

    if (!VALID_STATES.has(root.dataset.eomVirtualState) || root.dataset.eomVirtualState === "active") {
      root.dataset.eomVirtualState = "shell";
    }
  }

  function isPageManagerControllingMobileViewport() {
    const manager = window.EOMPageWindowManager;
    if (!manager) return false;

    try {
      const snapshot = manager.getSnapshot?.();
      if (snapshot && snapshot.enabled === false) return false;

      const maxWidth = Number(snapshot?.config?.mobileInitialHtmlReduction?.maxWidth || 749);
      return window.innerWidth <= maxWidth;
    } catch (error) {
      return window.innerWidth <= 749;
    }
  }

  function wrapController(root, controller = {}) {
    if (wrappedControllers.has(controller)) return wrappedControllers.get(controller);

    const wrapped = {
      hydrate() {
        const result = controller.hydrate?.();

        window.requestAnimationFrame(() => {
          normalizeAfterHydrate(root);
        });

        return result;
      },

      virtualize() {
        const result = controller.virtualize?.();

        window.requestAnimationFrame(() => {
          normalizeAfterVirtualize(root);
        });

        return result;
      },

      onEvaluate(details) {
        updatePositionFlags(root, details);
        controller.onEvaluate?.(details);

        if (!VALID_STATES.has(root.dataset.eomVirtualState)) {
          root.dataset.eomVirtualState = inferState(root);
        }

        if (!VALID_MODES.has(root.dataset.eomVirtualMode)) {
          root.dataset.eomVirtualMode = inferMode(root);
        }
      }
    };

    originalControllers.set(wrapped, controller);
    wrappedControllers.set(controller, wrapped);
    return wrapped;
  }

  function evaluate(root, controller) {
    if (!root || !controller) return;

    normalizeRoot(root);

    const rect = root.getBoundingClientRect();
    const loadMargin = numberFromDataset(root, "eomLoadMargin", 900);
    const virtualizeDistance = numberFromDataset(root, "eomVirtualizeDistance", 1400);
    const nearViewport = rect.top < window.innerHeight + loadMargin && rect.bottom > -loadMargin;
    const farAbove = rect.bottom < -virtualizeDistance;
    const farBelow = rect.top > window.innerHeight + virtualizeDistance;
    const details = { rect, nearViewport, farAbove, farBelow, loadMargin, virtualizeDistance };

    controller.onEvaluate?.(details);

    if (isPageManagerControllingMobileViewport()) {
      return;
    }

    if (nearViewport) {
      controller.hydrate?.();
      return;
    }

    if (farAbove || farBelow) {
      controller.virtualize?.();
    }
  }

  function evaluateAll() {
    scrollFrame = null;
    controllers.forEach((controller, root) => evaluate(root, controller));
  }

  function scheduleEvaluateAll() {
    if (scrollFrame) return;
    scrollFrame = window.requestAnimationFrame(evaluateAll);
  }

  const observer = "IntersectionObserver" in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const controller = controllers.get(entry.target);
          if (controller) evaluate(entry.target, controller);
        });
      }, {
        root: null,
        rootMargin: "900px 0px 1400px 0px",
        threshold: 0
      })
    : null;

  function decodeImages(root) {
    const images = Array.from(root.querySelectorAll("img"));

    return Promise.all(images.map((image) => {
      if (image.complete && image.naturalWidth > 0) return Promise.resolve();

      if (typeof image.decode === "function") {
        return image.decode().catch(() => undefined);
      }

      return new Promise((resolve) => {
        image.addEventListener("load", resolve, { once: true });
        image.addEventListener("error", resolve, { once: true });
      });
    }));
  }

  function createTemplateController(root) {
    const template = root.querySelector("[data-eom-virtual-template]");
    const mount = root.querySelector("[data-eom-virtual-mount]");

    if (!template || !mount) return null;

    root.dataset.eomVirtualMode = "template";

    return {
      hydrate() {
        const state = root.dataset.eomVirtualState;
        if (state === "active" || state === "hydrating") return;

        root.dataset.eomVirtualState = "hydrating";
        root.classList.remove("is-eom-height-locked");

        if (!mount.childElementCount) {
          mount.replaceChildren(template.content.cloneNode(true));
        }

        window.requestAnimationFrame(() => {
          root.dataset.eomVirtualState = "active";
          root.dispatchEvent(new CustomEvent("eom:section-hydrated", { bubbles: true }));
        });

        decodeImages(mount).then(() => {
          root.dispatchEvent(new CustomEvent("eom:section-images-ready", { bubbles: true }));
        });
      },

      virtualize() {
        if (root.dataset.eomEnableVirtualization === "false") return;
        if (root.dataset.eomVirtualState !== "active") return;

        if (shouldLockHeight(root)) {
          root.style.setProperty("--eom-locked-height", `${root.offsetHeight}px`);
          root.classList.add("is-eom-height-locked");
        }
        mount.replaceChildren();
        root.dataset.eomVirtualState = "shell";
        root.dispatchEvent(new CustomEvent("eom:section-virtualized", { bubbles: true }));
      }
    };
  }

  function createStaticController(root) {
    if (root.dataset.eomVirtualMode !== "static") return null;

    root.dataset.eomEnableVirtualization = "false";
    root.dataset.eomVirtualState = "active";

    return {
      hydrate() {
        root.dataset.eomVirtualState = "active";
        root.classList.remove("is-eom-height-locked");
      },

      virtualize() {
        root.dataset.eomVirtualState = "active";
      }
    };
  }

  function createVisualController(root) {
    if (root.dataset.eomVirtualMode !== "visual") return null;

    const slots = Array.from(root.querySelectorAll("[data-eom-visual-slot]"));
    if (!slots.length) return null;

    const getParts = (slot) => ({
      template: slot.querySelector("[data-eom-visual-template]"),
      mount: slot.querySelector("[data-eom-visual-mount]")
    });

    return {
      hydrate() {
        const state = root.dataset.eomVirtualState;
        if (state === "active" || state === "hydrating") return;

        root.dataset.eomVirtualState = "hydrating";
        root.classList.remove("is-eom-height-locked");

        slots.forEach((slot) => {
          const { template, mount } = getParts(slot);
          if (!template || !mount || mount.childElementCount) return;
          mount.replaceChildren(template.content.cloneNode(true));
        });

        window.requestAnimationFrame(() => {
          root.dataset.eomVirtualState = "active";
          root.dispatchEvent(new CustomEvent("eom:section-hydrated", { bubbles: true }));
        });
      },

      virtualize() {
        if (root.dataset.eomEnableVirtualization === "false") return;
        if (root.dataset.eomVirtualState !== "active") return;

        if (shouldLockHeight(root)) {
          root.style.setProperty("--eom-locked-height", `${root.offsetHeight}px`);
          root.classList.add("is-eom-height-locked");
        }

        slots.forEach((slot) => {
          const { mount } = getParts(slot);
          if (mount) mount.replaceChildren();
        });

        root.dataset.eomVirtualState = "shell";
        root.dispatchEvent(new CustomEvent("eom:section-virtualized", { bubbles: true }));
      }
    };
  }

  function createReportingController(root) {
    return {
      hydrate() {
        root.dataset.eomVirtualState = "active";
        root.dispatchEvent(new CustomEvent("eom:section-hydrated", { bubbles: true }));
      },

      virtualize() {
        if (root.dataset.eomEnableVirtualization === "false" || root.dataset.eomVirtualMode === "static") {
          root.dataset.eomVirtualState = "active";
          return;
        }

        root.dataset.eomVirtualState = inferState(root);
        root.dispatchEvent(new CustomEvent("eom:section-virtualized", { bubbles: true }));
      }
    };
  }

  function register(root, controller = {}) {
    if (!root) return;
    if (isSelfManagedMatrix(root)) return;

    normalizeRoot(root);

    const wrappedController = wrapController(root, controller);
    controllers.set(root, wrappedController);

    observer?.observe(root);
    evaluate(root, wrappedController);
  }

  function unregister(root) {
    if (!root) return;

    observer?.unobserve(root);
    controllers.delete(root);
  }

  function getController(root) {
    if (!root) return null;
    return controllers.get(root) || null;
  }

  function hydrate(root) {
    const controller = getController(root);
    if (!root || !controller) return false;

    normalizeRoot(root);
    controller.hydrate?.();
    return true;
  }

  function virtualize(root) {
    const controller = getController(root);
    if (!root || !controller) return false;

    normalizeRoot(root);
    controller.virtualize?.();
    return true;
  }

  function getSectionSnapshot(root) {
    if (!root) return null;

    normalizeRoot(root);

    return {
      state: root.dataset.eomVirtualState || "",
      mode: root.dataset.eomVirtualMode || "",
      enabled: root.dataset.eomEnableVirtualization !== "false",
      registered: controllers.has(root)
    };
  }

  function getRegisteredSections() {
    return Array.from(controllers.keys());
  }

  function getLoaderRegistry() {
    return JSON.parse(JSON.stringify(LOADER_CONTRACT_REGISTRY));
  }

  function getLoaderContractForSectionType(sectionType) {
    const loaderType = LOADER_CONTRACT_REGISTRY.sectionTypes[sectionType] || "static-text";
    const contract = LOADER_CONTRACT_REGISTRY.types[loaderType] || LOADER_CONTRACT_REGISTRY.types["static-text"];

    return {
      type: loaderType,
      ...contract,
      cssSource: LOADER_CONTRACT_REGISTRY.cssSource,
      jsSource: LOADER_CONTRACT_REGISTRY.jsSource,
      registryVersion: LOADER_CONTRACT_REGISTRY.version
    };
  }

  function registerEnhancementContract(root, options = {}) {
    if (!root) return null;

    if (root.__eomEnhancementContract && typeof root.__eomEnhancementContract.cleanup === "function") {
      root.__eomEnhancementContract.cleanup();
    }

    const cleanupTasks = [];
    const contractName = String(options.name || options.sectionType || root.dataset.eomSectionType || "section-enhancement");

    root.dataset.eomEnhancementContract = "registered";
    root.dataset.eomEnhancementContractName = contractName;
    root.dataset.eomEnhancementContractVersion = String(options.version || "phase-3.3");
    if (options.sectionType && !root.dataset.eomSectionType) {
      root.dataset.eomSectionType = String(options.sectionType);
    }

    const contract = {
      listen(target, type, handler, listenerOptions) {
        if (!target || !type || typeof handler !== "function") return () => {};
        target.addEventListener(type, handler, listenerOptions);
        const cleanup = () => target.removeEventListener(type, handler, listenerOptions);
        cleanupTasks.push(cleanup);
        return cleanup;
      },
      observe(observer, target) {
        if (!observer || !target || typeof observer.observe !== "function") return observer;
        observer.observe(target);
        cleanupTasks.push(() => {
          if (typeof observer.disconnect === "function") observer.disconnect();
        });
        return observer;
      },
      frame(callback) {
        const frameId = window.requestAnimationFrame(callback);
        cleanupTasks.push(() => window.cancelAnimationFrame(frameId));
        return frameId;
      },
      timeout(callback, delay) {
        const timeoutId = window.setTimeout(callback, delay);
        cleanupTasks.push(() => window.clearTimeout(timeoutId));
        return timeoutId;
      },
      addCleanup(task) {
        if (typeof task === "function") cleanupTasks.push(task);
      },
      cleanup() {
        while (cleanupTasks.length) {
          const task = cleanupTasks.pop();
          try { task(); } catch (error) {}
        }
        root.dataset.eomEnhancementContract = "cleaned";
      }
    };

    const onSectionUnload = (event) => {
      const target = event.target;
      const targetContainsRoot = target && typeof target.contains === "function" && target.contains(root);
      const rootContainsTarget = target && typeof root.contains === "function" && root.contains(target);
      if (target === root || targetContainsRoot || rootContainsTarget) {
        contract.cleanup();
      }
    };

    document.addEventListener("shopify:section:unload", onSectionUnload);
    cleanupTasks.push(() => document.removeEventListener("shopify:section:unload", onSectionUnload));
    root.__eomEnhancementContract = contract;

    return contract;
  }

  function initAutoSections(scope = document) {
    scope.querySelectorAll("[data-eom-virtual-section]").forEach((root) => {
      if (controllers.has(root)) return;
      if (isSelfManagedMatrix(root)) return;

      normalizeRoot(root);

      const controller =
        createTemplateController(root) ||
        createVisualController(root) ||
        createStaticController(root) ||
        createReportingController(root);

      register(root, controller);
    });
  }

  window.addEventListener("scroll", scheduleEvaluateAll, { passive: true });
  window.addEventListener("resize", scheduleEvaluateAll, { passive: true });
  document.addEventListener("shopify:section:load", (event) => initAutoSections(event.target));

  window.EOMVirtualSections = {
    register,
    unregister,
    hydrate,
    virtualize,
    getController,
    getSectionSnapshot,
    getRegisteredSections,
    getLoaderRegistry,
    getLoaderContractForSectionType,
    registerEnhancementContract,
    isPassiveUnderManager: isPageManagerControllingMobileViewport,
    evaluateAll: scheduleEvaluateAll,
    initAutoSections
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAutoSections());
  } else {
    initAutoSections();
  }
})();